import {
    AVAILABILITY_ERROR,
    AVAILABILITY_FETCH_START,
    AVAILABILITY_FETCH_SUCCESS,
    AVAILABILITY_FETCH_GROOMING_START,
    AVAILABILITY_FETCH_GROOMING_SUCCESS,
    AVAILABILITY_GET_GROOMING_NEXT_AVAILABLE_DATE_START,
    AVAILABILITY_GET_GROOMING_NEXT_AVAILABLE_DATE_SUCCESS,
    AVAILABILITY_GET_NEXT_DATE_START,
    AVAILABILITY_GET_NEXT_DATE_SUCCESS,
    AvailabilityAction
} from 'actions/availability/AvailabilityActionsTypes';
import {
    convertAvailabilityData,
    convertGroomingAvailabilityDate
} from 'actions/availability/AvailabilityDataConverter';
import { AvailabilityDtos, GroomingAvailableDateDto } from 'actions/availability/AvailabilityDtos';
import { AvailabilityError, AvailabilityErrorType } from 'actions/availability/AvailabilityErrors';
import { ApiPublicClient } from 'api/ApiClient';
import { Moment } from 'moment-timezone';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { RootAction } from 'types/Actions';
import { Availability, GroomingAvailableDate } from 'types/Availability';

const availabilityUrl = 'availability_by_hour';
const nextAvailableDateUrl = 'next_available_date';

const errorHandler = (error: unknown): AvailabilityAction => {
    console.error(error);

    return {
        type: AVAILABILITY_ERROR
    };
};

export const fetchStaffAvailabilityThunk = (
    date: Moment,
    staffIds: Array<number>,
    petsCount: number,
    durationInMinutes: number
): ThunkAction<Promise<Array<Availability>>, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        dispatch(fetchStart());

        const marketplace = getState().marketplace.data;

        try {
            const { data } = await ApiPublicClient.get<Array<AvailabilityDtos>>(availabilityUrl, {
                params: {
                    location_id: 'nil',
                    staff_ids: staffIds.join(','),
                    duration: durationInMinutes,
                    start_date: date.format('YYYY-MM-DD'),
                    number_of_pets: petsCount,
                    marketplace_id: marketplace.id
                }
            });

            const availability = convertAvailabilityData(data, marketplace.timeZone);

            dispatch(fetchSuccess(availability));

            return availability;
        } catch (error) {
            dispatch(errorHandler(error));

            throw new AvailabilityError(AvailabilityErrorType.Unknown);
        }
    };
};

export const getNextAvailableDateThunk = (
    startDate: Moment,
    petsCount: number,
    durationInMinutes: number,
    timeframeWeeks: number,
    staffIds?: Array<number>
): ThunkAction<Promise<Array<Availability>>, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        dispatch(getNextDateStart());

        const marketplace = getState().marketplace.data;

        const queryParams = {
            location_id: 'nil',
            duration: durationInMinutes,
            start_date: startDate.format('YYYY-MM-DD'),
            number_of_pets: petsCount,
            marketplace_id: marketplace.id,
            max_timeframe_weeks: timeframeWeeks
        } as Record<string, string | number | undefined>;

        if (staffIds?.length) {
            queryParams.staff_ids = staffIds.join(',');
        }

        try {
            const { data } = await ApiPublicClient.get<Array<AvailabilityDtos>>(
                nextAvailableDateUrl,
                {
                    params: queryParams
                }
            );

            const availability = convertAvailabilityData(data, marketplace.timeZone);

            dispatch(getNextDateSuccess(availability));

            return availability;
        } catch (error) {
            dispatch(errorHandler(error));

            throw new AvailabilityError(AvailabilityErrorType.Unknown);
        }
    };
};

export const fetchStaffsAvailabilityThunk = (
    date: Moment,
    staffIds: Array<number>,
    petsCount: number,
    durationInMinutes: number
): ThunkAction<Promise<Array<GroomingAvailableDate>>, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        dispatch({
            type: AVAILABILITY_FETCH_GROOMING_START
        });

        const marketplace = getState().marketplace.data;

        try {
            const { data } = await ApiPublicClient.get<Array<GroomingAvailableDateDto>>(
                availabilityUrl,
                {
                    params: {
                        location_id: 'nil',
                        staff_ids: staffIds.join(','),
                        duration: durationInMinutes,
                        start_date: date.format('YYYY-MM-DD'),
                        number_of_pets: petsCount,
                        marketplace_id: marketplace.id
                    }
                }
            );

            const availability = data.map(item =>
                convertGroomingAvailabilityDate(item, marketplace.timeZone)
            );

            dispatch({
                type: AVAILABILITY_FETCH_GROOMING_SUCCESS,
                payload: {
                    dates: availability
                }
            });

            return availability;
        } catch (error) {
            dispatch(errorHandler(error));

            throw error;
        }
    };
};

export const getStaffNextAvailableDateThunk = (
    startDate: Moment,
    petsCount: number,
    durationInMinutes: number,
    timeframeWeeks: number,
    staffIds?: Array<number>
): ThunkAction<Promise<GroomingAvailableDate>, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        dispatch({
            type: AVAILABILITY_GET_GROOMING_NEXT_AVAILABLE_DATE_START
        });

        const marketplace = getState().marketplace.data;

        const queryParams = {
            location_id: 'nil',
            duration: durationInMinutes,
            start_date: startDate.format('YYYY-MM-DD'),
            number_of_pets: petsCount,
            marketplace_id: marketplace.id,
            max_timeframe_weeks: timeframeWeeks
        } as Record<string, string | number | undefined>;

        if (staffIds?.length) {
            queryParams.staff_ids = staffIds.join(',');
        }

        try {
            const { data } = await ApiPublicClient.get<Array<GroomingAvailableDateDto>>(
                nextAvailableDateUrl,
                {
                    params: queryParams
                }
            );

            const availableDate = data.map(item =>
                convertGroomingAvailabilityDate(item, marketplace.timeZone)
            )[0];

            dispatch({
                type: AVAILABILITY_GET_GROOMING_NEXT_AVAILABLE_DATE_SUCCESS,
                payload: {
                    dates: availableDate
                }
            });

            return availableDate;
        } catch (error) {
            dispatch(errorHandler(error));

            throw error;
        }
    };
};

const fetchStart = (): AvailabilityAction => {
    return {
        type: AVAILABILITY_FETCH_START
    };
};

const fetchSuccess = (availableHours: Array<Availability>): AvailabilityAction => {
    return {
        type: AVAILABILITY_FETCH_SUCCESS,
        payload: {
            availableHours
        }
    };
};

const getNextDateStart = (): AvailabilityAction => {
    return {
        type: AVAILABILITY_GET_NEXT_DATE_START
    };
};

const getNextDateSuccess = (availableHours: Array<Availability>): AvailabilityAction => {
    return {
        type: AVAILABILITY_GET_NEXT_DATE_SUCCESS,
        payload: {
            availableHours
        }
    };
};
